function coerceStampTemplateIds(value) {
  const out = [];
  const push = (item) => {
    if (item == null) return;
    if (typeof item === 'object') {
      const id = String(item.id || item.templateId || '').trim();
      if (id) out.push(id);
      return;
    }
    String(item)
      .split(/[,，]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((id) => out.push(id));
  };
  if (Array.isArray(value)) value.forEach(push);
  else push(value);
  return out;
}

function isStampDesignStep(step) {
  const nav = String(step?.navId || '');
  const label = String(step?.label || '');
  return nav === 'podProductDesign' || label.includes('商品设计') || label.includes('套图');
}

/** 从步骤、分层和画布节点收集套图模板 id（步骤被规范化清空时仍能从画布读到） */
export function collectWorkflowStampTemplateIds(template) {
  const ids = [];
  const push = (value) => {
    coerceStampTemplateIds(value).forEach((id) => {
      if (!ids.includes(id)) ids.push(id);
    });
  };
  const visit = (step) => {
    if (!step || typeof step !== 'object') return;
    const raw = step.params?.imageTemplateIds ?? step.imageTemplateIds;
    if (raw == null) return;
    if (isStampDesignStep(step) || raw) push(raw);
  };
  (template?.steps || []).forEach(visit);
  (template?.stepStages || []).forEach((stage) => {
    (Array.isArray(stage) ? stage : []).forEach(visit);
  });
  (template?.canvas?.nodes || []).forEach(visit);
  return ids;
}

export const STAMP_ASSIGN_MODE = {
  PRE_ASSIGN_BY_ROW: 'preAssignByRow',
  RUNTIME_MANUAL: 'runtimeManual',
  AUTO_FILL: 'autoFill',
};

export function resolveWorkflowStampAssignStep(template) {
  const steps = template?.steps || [];
  return steps.find((s) => s.navId === 'podProductDesign') || null;
}

/** @returns {'preAssignByRow'|'runtimeManual'|'autoFill'} */
export function resolveStampAssignMode(params = {}) {
  const mode = String(params?.stampAssignMode || '').trim();
  if (mode === STAMP_ASSIGN_MODE.PRE_ASSIGN_BY_ROW
    || mode === STAMP_ASSIGN_MODE.RUNTIME_MANUAL
    || mode === STAMP_ASSIGN_MODE.AUTO_FILL) {
    return mode;
  }
  if (params?.requireManualStampAssign === false) return STAMP_ASSIGN_MODE.AUTO_FILL;
  if (params?.requireManualStampAssign === true) return STAMP_ASSIGN_MODE.RUNTIME_MANUAL;
  return STAMP_ASSIGN_MODE.PRE_ASSIGN_BY_ROW;
}

export function needsPreAssignAtTaskCreation(template) {
  const step = resolveWorkflowStampAssignStep(template);
  if (!step) return false;
  return resolveStampAssignMode(step.params) === STAMP_ASSIGN_MODE.PRE_ASSIGN_BY_ROW;
}

/**
 * 素材 → 印花位分配（按顺序成组，不覆盖）：
 * 2 位 + [r0,r1,r2,r3] → r0→slot0(g0), r1→slot1(g0), r2→slot0(g1), r3→slot1(g1)
 * @returns {Array<{ rowId: string, slotName: string, groupIndex: number }>}
 */
export function buildStampSlotAssignments(rowIds = [], slotNames = []) {
  const ids = rowIds.map(String).filter(Boolean);
  const slots = slotNames.filter(Boolean);
  if (!ids.length || !slots.length) return [];

  if (ids.length === 1) {
    return slots.map((slotName) => ({
      rowId: ids[0],
      slotName,
      groupIndex: 0,
    }));
  }

  const slotCount = slots.length;
  return ids.map((rowId, index) => ({
    rowId,
    slotName: slots[index % slotCount],
    groupIndex: Math.floor(index / slotCount),
  }));
}

/** @deprecated 多组时会丢映射；请优先用 buildStampSlotAssignments */
export function buildSlotToRowIdMap(rowIds = [], slotNames = []) {
  const map = new Map();
  buildStampSlotAssignments(rowIds, slotNames).forEach(({ rowId, slotName }) => {
    // 同名印花位多组时保留首个映射，供旧调用方兼容
    if (!map.has(slotName)) map.set(slotName, rowId);
  });
  return map;
}

/** @returns {'perRow'|'merged'} */
export function resolvePreAssignExecutionMode(rowIds = [], slotNames = []) {
  const ids = rowIds.map(String).filter(Boolean);
  const slots = slotNames.filter(Boolean);
  if (slots.length <= 1) return 'perRow';
  if (ids.length <= 1) return 'perRow';
  if (ids.length === slots.length) return 'merged';
  if (ids.length > 1 && slots.length > 1) return 'merged';
  return 'perRow';
}

export function getNeededRowIdsForPreAssign(stampPreAssign, slotNames = []) {
  const rowIds = (stampPreAssign?.rowIds || []).map(String).filter(Boolean);
  if (rowIds.length === 0) return [];
  const assignments = buildStampSlotAssignments(rowIds, slotNames);
  if (assignments.length === 0) return rowIds;
  return [...new Set(assignments.map((item) => item.rowId))];
}

export function resolvePreAssignExecutorRowIndex(stampPreAssign, rows = [], slotNames = []) {
  const needed = new Set(getNeededRowIdsForPreAssign(stampPreAssign, slotNames));
  for (let i = 0; i < rows.length; i += 1) {
    if (needed.has(String(rows[i]?.id))) return i;
  }
  const rowIds = (stampPreAssign?.rowIds || []).map(String).filter(Boolean);
  for (let i = 0; i < rows.length; i += 1) {
    if (rowIds.includes(String(rows[i]?.id))) return i;
  }
  return 0;
}

export function buildPreAssignGoodsForms({
  templates = [],
  rowIds = [],
  slotNames = [],
  rowStampOutputs = {},
}) {
  const assignments = buildStampSlotAssignments(rowIds, slotNames);
  if (assignments.length === 0) return [];

  const groupsByIndex = new Map();
  assignments.forEach(({ rowId, slotName, groupIndex }) => {
    const out = rowStampOutputs[String(rowId)];
    if (!out?.spaceId || !out?.url) return;
    const stampName = String(out.name || out.title || '').trim();
    if (!groupsByIndex.has(groupIndex)) groupsByIndex.set(groupIndex, {});
    groupsByIndex.get(groupIndex)[slotName] = {
      id: String(out.spaceId),
      url: String(out.url),
      role: 'print',
      ...(stampName ? { name: stampName } : {}),
    };
  });

  const imageGroups = [...groupsByIndex.keys()]
    .sort((a, b) => a - b)
    .map((key) => groupsByIndex.get(key))
    .filter((group) => group && Object.keys(group).length > 0);

  if (imageGroups.length === 0) return [];

  return templates.map((template) => ({
    templateId: template.id,
    templateName: template.name || `模版 ${template.id}`,
    templateData: template.templateData,
    imageGroups: imageGroups.map((group) => ({ ...group })),
    compositeResults: [],
  })).filter((form) => (form.imageGroups || []).length > 0);
}

export function buildPerRowPreAssignGoodsForms({
  templates = [],
  slotNames = [],
  rowId,
  rowIds = [],
  pipelineStamp,
}) {
  if (!pipelineStamp?.spaceId || !pipelineStamp?.url) return [];

  const assignments = buildStampSlotAssignments(rowIds, slotNames);
  const imageGroup = {};
  const stampName = String(pipelineStamp?.name || pipelineStamp?.title || '').trim();
  const stampRef = {
    id: String(pipelineStamp.spaceId),
    url: String(pipelineStamp.url),
    role: 'print',
    ...(stampName ? { name: stampName } : {}),
  };

  if (rowIds.length <= 1) {
    // 仅一张素材：灌满全部印花位
    slotNames.forEach((name) => { imageGroup[name] = stampRef; });
  } else {
    // 多素材：只填本行对应的印花位，禁止把同一张复制到其它位
    assignments.forEach(({ rowId: assignedRowId, slotName }) => {
      if (String(assignedRowId) === String(rowId)) {
        imageGroup[slotName] = stampRef;
      }
    });
    if (Object.keys(imageGroup).length === 0 && slotNames.length === 1) {
      imageGroup[slotNames[0]] = stampRef;
    }
  }

  if (Object.keys(imageGroup).length === 0) return [];

  return templates.map((template) => ({
    templateId: template.id,
    templateName: template.name || `模版 ${template.id}`,
    templateData: template.templateData,
    imageGroups: [{ ...imageGroup }],
    compositeResults: [],
  }));
}

export async function loadWorkflowStampTemplates(templateIds = []) {
  const ids = [...new Set(templateIds.map(String).filter(Boolean))];
  if (ids.length === 0) return [];
  const byId = new Map();
  try {
    const { fetchImageTemplateCatalogEntries } = await import('@/pages/ipe/workbench/utils/templateListUtils');
    const catalog = await fetchImageTemplateCatalogEntries();
    (Array.isArray(catalog) ? catalog : []).forEach((tpl) => {
      if (tpl?.id != null) byId.set(String(tpl.id), tpl);
    });
  } catch {
    /* 目录失败时走单条查询 */
  }

  const missing = ids.filter((id) => !byId.has(id));
  if (missing.length > 0) {
    const { templateAPI } = await import('@/utils/api');
    await Promise.all(missing.map(async (id) => {
      try {
        const res = await templateAPI.getById(id);
        const tpl = res?.template || res?.data || (res?.id ? res : null);
        if (tpl?.id != null) byId.set(String(tpl.id), tpl);
      } catch {
        /* 官方套图不在 /templates，目录里没有就跳过 */
      }
    }));
  }
  return ids.map((id) => byId.get(id)).filter(Boolean);
}

export function summarizePreAssignPlan({ rowIds = [], slotNames = [], materialLabels = [] }) {
  const map = buildSlotToRowIdMap(rowIds, slotNames);
  return [...map.entries()].map(([slotName, rowId]) => {
    const index = rowIds.findIndex((id) => String(id) === String(rowId));
    const label = materialLabels[index] || `素材 ${index + 1}`;
    return { slotName, rowId, materialLabel: label };
  });
}
